import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import type { AdminPuzzleBulkResult } from '@chessx/shared/types'

import { useBulkUploadPuzzles } from './PuzzlesApi'
import { PUZZLE_TEMPLATE, downloadJson, parseBulkJson } from './puzzleJson'

interface PuzzleBulkUploadProps {
  show: boolean
  onHide: () => void
}

interface Progress {
  done: number
  total: number
}

const PuzzleBulkUpload = ({ show, onHide }: PuzzleBulkUploadProps) => {
  const [fileName, setFileName] = useState('')
  const [puzzles, setPuzzles] = useState<unknown[] | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [result, setResult] = useState<AdminPuzzleBulkResult | null>(null)
  const upload = useBulkUploadPuzzles((done, total) => setProgress({ done, total }))

  useEffect(() => {
    if (!show) return
    setFileName('')
    setPuzzles(null)
    setParseError(null)
    setProgress(null)
    setResult(null)
  }, [show])

  const readFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setResult(null)
    try {
      setPuzzles(parseBulkJson(await file.text()))
      setParseError(null)
    } catch (error) {
      setPuzzles(null)
      setParseError(error instanceof Error ? error.message : String(error))
    }
  }

  const startUpload = async () => {
    if (!puzzles) return
    const summary = await upload.mutateAsync(puzzles).catch(() => null)
    if (summary) setResult(summary)
  }

  return (
    <Modal centered size="lg" show={show} onHide={onHide} backdrop="static">
      <div className="pz-modal">
        <h4 className="pz-modal-title">Bulk upload puzzles</h4>
        <p className="pz-modal-body">
          Upload a JSON array of puzzles, or an object with a <code>puzzles</code> array. Each entry
          uses the same shape as the editor&apos;s raw JSON. Entries are matched by{' '}
          <code>puzzleKey</code>: existing keys are replaced, new keys are created.
        </p>
        <button
          type="button"
          className="pz-btn pz-btn-ghost"
          onClick={() => downloadJson('puzzle-template.json', [PUZZLE_TEMPLATE])}
        >
          Download template
        </button>

        <label className="pz-dropzone">
          <input type="file" accept="application/json,.json" onChange={readFile} />
          <span>{fileName || 'Choose a .json file'}</span>
        </label>

        {parseError && <div className="pz-error">{parseError}</div>}
        {puzzles && !result && (
          <div className="pz-note">{puzzles.length} puzzles ready to upload</div>
        )}
        {progress && upload.isPending && (
          <div className="pz-progress">
            <div
              className="pz-progress-bar"
              style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }}
            />
            <span>
              {progress.done} / {progress.total}
            </span>
          </div>
        )}

        {result && (
          <div className="pz-result">
            <div className="pz-result-stats">
              <span className="pz-badge pz-badge-ok">{result.inserted} inserted</span>
              <span className="pz-badge pz-badge-info">{result.updated} updated</span>
              <span className={`pz-badge ${result.failed.length ? 'pz-badge-bad' : ''}`}>
                {result.failed.length} failed
              </span>
            </div>
            {result.failed.length > 0 && (
              <ul className="pz-failures">
                {result.failed.map(failure => (
                  <li key={failure.index}>
                    <code>
                      #{failure.index}
                      {failure.puzzleKey ? ` ${failure.puzzleKey}` : ''}
                    </code>{' '}
                    {failure.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="pz-modal-actions">
          <button type="button" className="pz-btn pz-btn-ghost" onClick={onHide}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              className="pz-btn pz-btn-primary"
              disabled={!puzzles?.length || upload.isPending}
              onClick={startUpload}
            >
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default PuzzleBulkUpload
